<script setup>
import { computed } from "vue";
import { Link } from "@inertiajs/vue3";
import { useClipboard } from "@vueuse/core";
import { useShareLink } from "@/composables/useShareLink";
import { useModalogStore } from "@/stores/modalog.js";
import BrandThrobber from "@/Components/Brand/BrandThrobber.vue";
import MoveItemMenu from "./MoveItemMenu.vue";
import { retailerDisplayName, retailerHost } from "@/utilities/retailer.js";

const props = defineProps({
    item: {
        type: Object,
        required: true,
    },
    list: {
        type: Object,
        required: true,
    },
    // Confirmed-gifts (spoiler) view: moving is disabled and the purchaser
    // is shown instead of the retailer line.
    spoiled: {
        type: Boolean,
        default: false,
    },
    // Visitors get an "Added" date line instead of the action footer.
    isOwner: {
        type: Boolean,
        default: true,
    },
    // Flag fully-confirmed items on the visitor confirmed view.
    confirmed: {
        type: Boolean,
        default: false,
    },
    // Overrides the spoiled "Confirmed by" line, e.g. "Confirmed for Kyle"
    // on the Confirmed By Me view (GH-878).
    confirmationLabel: {
        type: String,
        default: null,
    },
});

const { showModalog } = useModalogStore();

const itemUrl = computed(() => route("item.detail", { item: props.item.uuid }));

const { copy, copied } = useClipboard({ source: itemUrl });
const { shareLink } = useShareLink();

// Tag the link so a signup through it is attributed back (GH-1192).
function copyItemLink() {
    if (isParsing.value) {
        return;
    }

    copy(shareLink(itemUrl.value, { type: "item", uuid: props.item.uuid }));
}

const imageUrl = computed(
    () => props.item.image_url?.img_2x ?? props.item.image_url?.img_1x ?? null,
);

const isParsing = computed(() => props.item.status === 0);

// A parsing item has nothing worth opening yet, so the card goes inert: the
// image and title stop being links and every footer action is disabled until
// the poll brings the parsed item back (GH-1412).
const linkTag = computed(() => (isParsing.value ? "div" : Link));
const linkHref = computed(() => (isParsing.value ? undefined : itemUrl.value));

const needsEditing = computed(
    () => props.item.edit_status > 0 && props.item.edit_status !== 4,
);

const price = computed(() =>
    props.item.price ? `${props.item.price_symbol}${props.item.price}` : null,
);

// "2026-07-10 09:12:00" → "7/10/26"
const addedLabel = computed(() => {
    const [date] = (props.item.created_at ?? "").split(" ");
    const [year, month, day] = date.split("-");

    return year
        ? `Added ${Number(month)}/${Number(day)}/${year.slice(2)}`
        : null;
});

// Retailer hostname ("apple.com"), falling back to the product URL's host.
// item.url is the affiliate link, so it's never shown. Custom gifts carry the
// "custom_item" placeholder retailer, which must never be shown.
const sourceLabel = computed(() => {
    const retailer = props.item.product?.retailer;
    const label = retailerHost(retailer) ?? retailerDisplayName(retailer);

    if (label) {
        return label;
    }

    try {
        return new URL(props.item.product?.url).hostname.replace(/^www\./, "");
    } catch {
        return null;
    }
});

function deleteItem() {
    if (isParsing.value) {
        return;
    }

    showModalog({
        type: "ModalogDeleteItem",
        props: { item: props.item },
    });
}
</script>

<template>
    <article
        v-if="!item.is_deleted"
        class="relative flex flex-col rounded-[4px] bg-white"
        :class="isParsing && 'cursor-not-allowed'"
        :aria-busy="isParsing ? 'true' : undefined"
    >
        <!-- Parsing treatment (GH-988, ported from main): a shimmering
             gradient ring hugs the card edge while a soft white veil dims
             the content. The ring is the padding of a masked overlay —
             mask-composite carves out everything but the 2px border. -->
        <template v-if="isParsing">
            <span
                aria-hidden="true"
                class="shimmer-ring pointer-events-none absolute inset-0 z-20 animate-shimmer rounded-[4px]"
            ></span>
            <span
                aria-hidden="true"
                class="pointer-events-none absolute inset-0 z-[5] rounded-[4px] bg-white/35"
            ></span>
        </template>
        <component
            :is="linkTag"
            :href="linkHref"
            class="relative m-[15px] mb-0 block max-lg:m-4 max-lg:mb-0"
            :title="item.name"
        >
            <div
                class="aspect-[219/154] w-full overflow-hidden rounded-[2px] bg-[#f4f4f6]"
            >
                <img
                    v-if="imageUrl"
                    :src="imageUrl"
                    :alt="item.name"
                    class="size-full object-cover"
                />
            </div>
            <span
                v-if="price"
                class="absolute bottom-2 right-2 rounded-[2px] px-2.5 py-1 font-brand text-[12px] font-bold max-lg:bottom-3 max-lg:right-3 max-lg:text-[15px]"
                :class="
                    isOwner
                        ? 'bg-white/75 text-black'
                        : 'bg-black/50 text-white'
                "
            >
                {{ price }}
            </span>
            <span
                v-if="isParsing"
                class="absolute left-2 top-2 z-10 inline-flex items-center gap-1.5 rounded-[2px] bg-brand-navy/80 px-2.5 py-1 font-brand text-[11px] font-semibold text-white"
            >
                Fetching data
                <BrandThrobber variant="mono" />
            </span>
            <span
                v-else-if="needsEditing && isOwner"
                class="absolute left-2 top-2 rounded-[2px] bg-brand-red px-2.5 py-1 font-brand text-[11px] font-semibold text-white"
            >
                Missing Info
            </span>
        </component>

        <div
            class="flex min-w-0 flex-1 flex-col px-4 pb-3 pt-3.5 max-lg:pb-5 max-lg:pt-6"
        >
            <component
                :is="linkTag"
                :href="linkHref"
                class="line-clamp-2 font-brand text-[18px] font-semibold leading-[1.3] text-brand-navy transition max-lg:min-h-[58px] max-lg:text-[22px]"
                :class="!isParsing && 'hover:text-brand-red'"
            >
                {{ item.name }}
            </component>
            <p
                v-if="confirmationLabel || (spoiled && item.purchaser)"
                class="mt-1 truncate font-brand text-[11px] font-semibold text-[#565a77] max-lg:text-[13px]"
            >
                {{
                    confirmationLabel ??
                    `Confirmed by ${item.purchaser.presentation_name}`
                }}
            </p>
            <p
                v-else-if="sourceLabel"
                class="mt-1 truncate font-brand text-[11px] text-[#8487a1] max-lg:mt-2 max-lg:text-[13px]"
            >
                {{ sourceLabel }}
            </p>
        </div>

        <footer
            v-if="!isOwner"
            class="border-t border-solid border-black/10 px-4 py-2.5"
        >
            <p class="font-brand text-[11px] text-[#8487a1] max-lg:text-[13px]">
                {{ addedLabel }}
            </p>
        </footer>

        <footer
            v-else
            class="flex items-center gap-1 border-t border-solid border-black/10 px-2.5 py-1.5 max-lg:px-3.5 max-lg:py-2.5"
        >
            <button
                type="button"
                class="inline-flex items-center gap-1.5 rounded-[4px] px-1.5 py-1.5 font-brand text-[13px] font-semibold text-[#565a77] transition hover:bg-brand-cream hover:text-brand-navy disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-[#565a77]"
                :disabled="isParsing"
                @click="copyItemLink()"
            >
                <svg
                    viewBox="0 0 16 18"
                    class="h-[13px] w-[11px] max-lg:h-[15px] max-lg:w-[13px]"
                    fill="currentColor"
                    aria-hidden="true"
                >
                    <path
                        d="M13.5 0H5.4C4.41 0 3.6 0.81 3.6 1.8V12.6C3.6 13.59 4.41 14.4 5.4 14.4H13.5C14.49 14.4 15.3 13.59 15.3 12.6V1.8C15.3 0.81 14.49 0 13.5 0ZM13.5 12.6H5.4V1.8H13.5V12.6ZM0 11.7V9.9H1.8V11.7H0ZM0 6.75H1.8V8.55H0V6.75ZM6.3 16.2H8.1V18H6.3V16.2ZM0 14.85V13.05H1.8V14.85H0ZM1.8 18C0.81 18 0 17.19 0 16.2H1.8V18ZM4.95 18H3.15V16.2H4.95V18ZM9.45 18V16.2H11.25C11.25 17.19 10.44 18 9.45 18ZM1.8 3.6V5.4H0C0 4.41 0.81 3.6 1.8 3.6Z"
                    />
                </svg>
                {{ copied ? "Copied!" : "Copy URL" }}
            </button>

            <MoveItemMenu
                v-if="!spoiled"
                :item="item"
                :list="list"
                :disabled="isParsing"
                align="left"
                class="ml-4"
            />

            <button
                type="button"
                class="ml-auto grid size-8 place-items-center rounded-[4px] text-[#565a77] transition hover:bg-brand-cream hover:text-brand-red disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-[#565a77]"
                aria-label="Delete gift"
                :disabled="isParsing"
                @click="deleteItem"
            >
                <svg
                    viewBox="0 0 13 14"
                    class="h-3.5 w-[13px] max-lg:h-[17px] max-lg:w-[15px]"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.12"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                >
                    <path
                        d="M10.3805 2.61863V9.61381C10.3805 10.7661 10.3805 11.3422 10.1512 11.7823C9.94947 12.1695 9.6276 12.4842 9.23167 12.6814C8.78151 12.9057 8.19235 12.9057 7.01387 12.9057H5.33054C4.1521 12.9057 3.56287 12.9057 3.11277 12.6814C2.71684 12.4842 2.39495 12.1695 2.19321 11.7823C1.96387 11.3422 1.96387 10.7661 1.96387 9.61381V2.61863M0.561096 2.61863H11.7833M8.97776 2.61863L8.78797 2.0618C8.60399 1.5222 8.51197 1.2524 8.34139 1.05292C8.19073 0.876777 7.99729 0.740432 7.77874 0.656394C7.53122 0.561218 7.24042 0.561218 6.65869 0.561218H5.68572C5.10399 0.561218 4.8132 0.561218 4.56568 0.656394C4.34714 0.740432 4.15367 0.876777 4.00302 1.05292C3.83241 1.2524 3.74044 1.5222 3.55648 2.0618L3.36665 2.61863"
                    />
                </svg>
            </button>
        </footer>

        <!-- Confirmed-gifts (spoiler) view: purchased banner (per Figma) -->
        <div
            v-if="confirmed"
            class="m-[15px] mt-0 rounded-[2px] bg-[#eb3700] py-1.5 text-center font-brand text-[12px] font-bold uppercase tracking-wide text-white"
        >
            Purchased
        </div>
    </article>
</template>

<style scoped>
/* The animated border is the padding of a masked overlay: the two-layer
   mask (content-box + full box) composited with exclude leaves only the
   2px ring painted. Declared here rather than as utilities because the
   mask shorthand must come before mask-composite or it resets it. */
.shimmer-ring {
    padding: 2px;
    background-image: linear-gradient(
        120deg,
        #ffb545 0%,
        #fff 30%,
        #f21439 60%,
        #fff 100%
    );
    background-size: 200% 200%;
    -webkit-mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
    mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
}
</style>
